import {
  Company_User_Role,
  Company_User_Status,
  CompanyUsersDocument,
  useUpdateCompanyUserRoleMutation,
} from "@generated/graphql";
import { Box, Chip, SelectChangeEvent, Stack, Typography } from "@mui/material";
import Select from "components/inputs/Select";
import { COMPANY_USER_ROLE_TITLE } from "constants/company";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useContext } from "react";
import TEAM_ROLE_OPTIONS from "../constants";
import { TeamMember } from "./tableConfig/types";

type TeamMemberRoleSelectProps = {
  employee: TeamMember;
};

const TeamMemberRoleSelect = ({ employee }: TeamMemberRoleSelectProps) => {
  const { addToast } = useContext(ToastContext);
  const [updateCompanyUserRole, { loading }] = useUpdateCompanyUserRoleMutation();
  const selectedRole = TEAM_ROLE_OPTIONS.find((role) => role.value === employee.role);
  const isRoleChangeDisabled = loading || employee.status === Company_User_Status.Deactivated;

  const handleRoleChange = (event: SelectChangeEvent<unknown>) => {
    const role = event.target.value as Company_User_Role;
    if (role === employee.role) return;

    updateCompanyUserRole({
      variables: {
        input: {
          companyUserId: employee.id,
          role,
        },
      },
      refetchQueries: [CompanyUsersDocument],
      awaitRefetchQueries: true,
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS }))
      .catch((error) => parseApolloError(error, { addToast }));
  };

  if (!selectedRole) {
    return (
      <Chip
        label={COMPANY_USER_ROLE_TITLE[employee.role]}
        sx={{ backgroundColor: "#F5F3FF", color: "#7C3AED", "& .MuiChip-label": { fontWeight: 600 } }}
      />
    );
  }

  const renderRole = (role: (typeof TEAM_ROLE_OPTIONS)[number]) => (
    <Stack
      component='span'
      direction='row'
      spacing={0.75}
      alignItems='center'>
      <Box
        component='span'
        sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: role.color }}
      />
      <Typography
        component='span'
        sx={{ color: role.color, fontWeight: 600 }}>
        {role.label}
      </Typography>
    </Stack>
  );

  return (
    <Select
      items={TEAM_ROLE_OPTIONS}
      value={employee.role}
      valueName='value'
      labelName='label'
      variant='outlined'
      disabled={isRoleChangeDisabled}
      onChange={handleRoleChange}
      renderValue={renderRole}
      renderItem={renderRole}
      sx={{
        backgroundColor: selectedRole.backgroundColor,
        borderRadius: 999,
        "& .MuiSelect-select": {
          py: 0.5,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "&.Mui-disabled": {
          opacity: 1,
        },
        "&.Mui-disabled .MuiSelect-select": {
          WebkitTextFillColor: "inherit",
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: 2,
            p: 0.5,
          },
        },
      }}
    />
  );
};

export default TeamMemberRoleSelect;
